// /src/components/donate/supportChoice/useSupportChoiceSocket.js

import { useCallback, useEffect, useRef } from 'react';
import { supportChoiceSocket } from '../../../../utils/Socket';
import { resumeAll, registerSocket } from '../../../wsRecovery/wsResume';
import { startHeartbeat, stopHeartbeat } from '../../../wsRecovery/heartbeat';
import {
  fetchMySupportStatusForPopup,
  getSupportChoiceDiff,
  supportChoiceLastSeen,
} from './supportChoiceApi';

const RESUME_REGISTRY_KEY = 'support-choice';

export default function useSupportChoiceSocket({
  enabled,
  isLoggedIn,
  isViewAsActive = false,
  viewAsUserId = null,
  viewAsLoading = false,
  onSupportStatus,
}) {
  const lastResumeIdRef = useRef(0);
  const viewAsActiveRef = useRef(isViewAsActive);

  useEffect(() => {
    viewAsActiveRef.current = isViewAsActive;
  }, [isViewAsActive]);

  const syncLastSeen = useCallback(async (wsLogId) => {
    const seq = Number(wsLogId);
    if (!Number.isFinite(seq) || seq <= 0) return;
    await supportChoiceLastSeen(seq).catch(() => {});
    lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seq);
  }, []);

  const refetchAndSyncPointer = useCallback(async () => {
    const data = await fetchMySupportStatusForPopup();
    if (data?.ok && typeof onSupportStatus === 'function') {
      onSupportStatus(data.data || null);
    }
    if (viewAsActiveRef.current) return;
    const diff = await getSupportChoiceDiff().catch(() => null);
    const maxSeq = Number(diff?.maxWsLogSeq || 0);
    if (maxSeq > 0) {
      await syncLastSeen(maxSeq);
    }
  }, [onSupportStatus, syncLastSeen]);

  const applyWsPayload = useCallback(async (payload) => {
    // Live WS is scoped to the authenticated user; ignore while inspecting another subject.
    if (viewAsActiveRef.current) return;

    // Always refetch full status so lifetime / active cards stay accurate in the popup.
    if (payload?.hasPendingChoice || payload?.wsLogId) {
      await refetchAndSyncPointer();
      return;
    }

    if (typeof onSupportStatus === 'function') {
      onSupportStatus({
        hasPendingChoice: false,
        pendingChoiceOptions: [],
        pendingTransactionCount: 0,
      });
    }

    const wsLogId = Number(payload?.wsLogId);
    if (wsLogId > 0) {
      await syncLastSeen(wsLogId);
    }
  }, [onSupportStatus, refetchAndSyncPointer, syncLastSeen]);

  useEffect(() => {
    registerSocket(RESUME_REGISTRY_KEY, () => {
      if (!isLoggedIn || !enabled) return;
      if (!supportChoiceSocket.connected) {
        supportChoiceSocket.connect();
        startHeartbeat(supportChoiceSocket);
      }
      refetchAndSyncPointer().catch((err) => {
        console.error('[SupportChoice] resume refetch failed:', err);
      });
    });
  }, [enabled, isLoggedIn, refetchAndSyncPointer]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;

    if (!supportChoiceSocket.connected) {
      supportChoiceSocket.connect();
      startHeartbeat(supportChoiceSocket);
    }

    const onConnect = () => {
      supportChoiceSocket.emit('joinSupportChoice');
      resumeAll();
    };

    const onReconnect = async () => {
      supportChoiceSocket.emit('joinSupportChoice');
      try {
        if (viewAsActiveRef.current) {
          await refetchAndSyncPointer();
          return;
        }
        const diff = await getSupportChoiceDiff();
        if (diff?.needRefresh || diff?.hasPendingChoice) {
          await refetchAndSyncPointer();
        } else if (diff?.maxWsLogSeq) {
          lastResumeIdRef.current = Math.max(
            lastResumeIdRef.current,
            Number(diff.maxWsLogSeq),
          );
        }
      } catch (err) {
        console.error('[SupportChoice] reconnect diff failed:', err);
      }
    };

    supportChoiceSocket.on('connect', onConnect);
    supportChoiceSocket.on('reconnect', onReconnect);

    return () => {
      stopHeartbeat(supportChoiceSocket);
      supportChoiceSocket.off('connect', onConnect);
      supportChoiceSocket.off('reconnect', onReconnect);
    };
  }, [isLoggedIn, enabled, refetchAndSyncPointer]);

  useEffect(() => {
    const handler = () => {
      if (!isLoggedIn || !enabled) return;
      if (!supportChoiceSocket.connected) {
        supportChoiceSocket.connect();
        startHeartbeat(supportChoiceSocket);
      }
    };
    window.addEventListener('auth-ws-refreshed', handler);
    return () => window.removeEventListener('auth-ws-refreshed', handler);
  }, [isLoggedIn, enabled]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;

    const resyncIfStale = async () => {
      try {
        if (viewAsActiveRef.current) {
          await refetchAndSyncPointer();
          return;
        }
        const diff = await getSupportChoiceDiff();
        if (diff?.needRefresh || diff?.hasPendingChoice) {
          await refetchAndSyncPointer();
        }
      } catch (err) {
        console.error('[SupportChoice] visibility/online diff failed:', err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      resyncIfStale();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', resyncIfStale);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', resyncIfStale);
    };
  }, [isLoggedIn, enabled, refetchAndSyncPointer]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;

    if (!supportChoiceSocket.connected) {
      supportChoiceSocket.connect();
      startHeartbeat(supportChoiceSocket);
    }

    supportChoiceSocket.emit('joinSupportChoice');

    const onMissed = async () => {
      try {
        await refetchAndSyncPointer();
      } catch (err) {
        console.error('[SupportChoice] supportChoiceMissed refetch failed:', err);
      }
    };

    const onUpdate = async (payload) => {
      try {
        await applyWsPayload(payload);
      } catch (err) {
        console.error('[SupportChoice] supportChoiceUpdate apply failed:', err);
      }
    };

    supportChoiceSocket.on('supportChoiceMissed', onMissed);
    supportChoiceSocket.on('supportChoiceUpdate', onUpdate);

    return () => {
      supportChoiceSocket.off('supportChoiceMissed', onMissed);
      supportChoiceSocket.off('supportChoiceUpdate', onUpdate);
      supportChoiceSocket.emit('leaveSupportChoice');
    };
  }, [isLoggedIn, enabled, refetchAndSyncPointer, applyWsPayload]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return undefined;
    if (isViewAsActive && viewAsLoading) return undefined;
    refetchAndSyncPointer().catch(() => {});
    return undefined;
  }, [
    isLoggedIn,
    enabled,
    isViewAsActive,
    viewAsUserId,
    viewAsLoading,
    refetchAndSyncPointer,
  ]);

  return { lastResumeIdRef, refetchAndSyncPointer };
}
